<i18n>
{
	"en": {
		"consignor": "Consignor",
		"despatchParty": "Despatch party",
		"consignee": "Consignee",
		"deliveryParty": "Delivery party"
	},
	"no": {
		"consignor": "Avsender",
		"despatchParty": "Henteadresse",
		"consignee": "Mottaker",
		"deliveryParty": "Leveringsadresse"
	}
}
</i18n>
<template>
	<div class="container">

		<GlobalAddressInput
			ref="consignor"
			:title="$t('consignor')"
			:value="order.consignor"
			:disabled="isSubOrder || orderIsLocked"
			disableButtonTabbing
			focusNextOnSelected
			@change="onChangeConsignor"
		/>

		<GlobalAddressInput
			ref="despatchParty"
			:title="$t('despatchParty')"
			:value="order.despatchParty"
			style="grid-area: 2 / 1"
			:disabled="isFirstSubOrder || orderIsLocked"
			disableButtonTabbing
			focusNextOnSelected
			@change="onChangeDespatchParty"
		/>

		<GlobalAddressInput
			ref="consignee"
			:title="$t('consignee')"
			:value="order.consignee"
			:disabled="isSubOrder || orderIsLocked"
			disableButtonTabbing
			focusNextOnSelected
			@change="onChangeConsignee"
		/>

		<GlobalAddressInput
			ref="deliveryParty"
			:title="$t('deliveryParty')"
			:value="order.deliveryParty"
			style="grid-area: 2 / 2"
			disableButtonTabbing
			:disabled="isLastSubOrder || orderIsLocked"
			focusNextOnSelected
			@change="onChangeDeliveryParty"
		/>

		<div class="incoterms">
			<IncotermInput
				ref="incoterm"
				class="incoterms-code"
				:value="incoterm"
				:disabled="isSubOrder || orderIsLocked"

				@change="onSelectIncoterm"
			/>

			<IncotermsPostalPlaceInput
				ref="incotermsPlace"
				:value="{ postalPlace: incotermsPlace }"
				:disabled="isSubOrder || orderIsLocked"

				@change="$emit('setIncotermsPlace', $event.postalPlace)
					&& focusNext({ target: getFirstInputElementByRef($refs.incotermsPlace) })"
			/>
		</div>

		<CustomerInput
			ref="customer"
			:value="order.customer"
			:disabled="isSubOrder || orderIsLocked"
			disableButtonTabbing

			@change="onChangeCustomer($event) || Object.keys($event).length
				&& focusNext({ target: getFirstInputElementByRef($refs.customer) })"
		/>
		<ConfirmEditAddress
			:isShown="confirmEditAddressVisible"
			:order="order"
			:ediSent="ediSent"
			@confirm="onConfirmEditAddress"
			@cancel="cancelEditAddress"
			@close="confirmEditAddressVisible = false"
		/>
	</div>
</template>

<script>
import { dateExists } from '@/core';
import IncotermsPostalPlaceInput from '@/modules/incoterms/IncotermsPostalPlaceInput';
import {
	mapGetters
} from '@/modules/newOrder/store/newOrder.store';
import IncotermInput from '@/modules/incoterms/IncotermInput';
import CustomerInput from '@/modules/customers/CustomerInput';
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';
import ConfirmEditAddress from '@/modules/newOrder/modals/ConfirmEditAddress';

export default {
	name: 'CustomerAndAddresses',
	components: {
		GlobalAddressInput,
		CustomerInput,
		IncotermInput,
		IncotermsPostalPlaceInput,
		ConfirmEditAddress
	},
	props: {
		order: {
			type: Object,
			required: true,
		},
		isSubOrder: {
			type: Boolean,
			default: false
		},
		incoterm: {
			type: Object,
			required: false
		},
		incotermsPlace: String,
	},
	data() {
		return {
			shouldSelectAllDespatchParty: false,
			shouldSelectAllDeliveryParty: false,
			shouldSelectAllIncotermsPlace: false,
			confirmEditAddressVisible: false,
			address: null,
			originalAddress: null,
			emitCode: ''
		};
	},
	computed: {
		...mapGetters(['orderIsLocked']),
		isFirstSubOrder() {
			return this.isSubOrder && (!this.order.prev || this.order.prev.length === 0);
		},
		isLastSubOrder() {
			return this.isSubOrder && (!this.order.next || this.order.next.length === 0);
		},
		ediSent() {
			return this.order && this.order.edi;
		},
		actualDatesSet() {
			return this.order && (dateExists(this.order.atd) || dateExists(this.order.ata));
		}
	},
	updated() {
		this.$nextTick(
			() => {

				if (this.shouldSelectAllDespatchParty) {
					let element = this.getFirstInputElementByRef(this.$refs.despatchParty);
					element.select();
					this.shouldSelectAllDespatchParty = false;
				}

				if (this.shouldSelectAllDeliveryParty) {
					let element = this.getFirstInputElementByRef(this.$refs.deliveryParty);
					element.select();
					this.shouldSelectAllDeliveryParty = false;
				}

				if (this.shouldSelectAllIncotermsPlace) {
					let element = this.getFirstInputElementByRef(this.$refs.incotermsPlace);
					element.select();
					this.shouldSelectAllIncotermsPlace = false;
				}
			}
		);
	},
	methods: {
		getFirstInputElementByRef(ref) {
			return ref.$el.querySelector('input');
		},
		onSelectIncoterm(incoterm) {

			this.$emit('setIncotermsCode', incoterm && incoterm.code);

			if (!incoterm)
				return;

			this.shouldSelectAllIncotermsPlace = true;

			if (
				!this.order.customer.id // Dont overwrite
				&& incoterm.getCustomerFrom === 'S' // S for avSender
				&& this.order.consignor
				&& this.order.consignor.customerNumber
			)
				this.$emit('updateCustomerByNumber', this.order.consignor.customerNumber);

			else if (
				!this.order.customer.id // Dont overwrite
				&& incoterm.getCustomerFrom === 'M' // M for Mottaker
				&& this.order.consignee
				&& this.order.consignee.customerNumber
			)
				this.$emit('updateCustomerByNumber', this.order.consignee.customerNumber);


			if (
				!this.incotermsPlace // Dont overwrite
				&& incoterm.getAddressFrom === 'S' // S for avSender
				&& this.order.consignor
				&& this.order.consignor.postalPlace
			)
				this.$emit('setIncotermsPlace', this.order.consignor.postalPlace);

			else if (
				!this.incotermsPlace // Dont overwrite
				&& incoterm.getAddressFrom === 'M' // M for Mottaker
				&& this.order.consignee
				&& this.order.consignee.postalPlace
			)
				this.$emit('setIncotermsPlace', this.order.consignee.postalPlace);
		},

		onChangeCustomer(customer) {
			this.$emit('changeCustomer', customer);

			if (
				!this.order.consignor.id
				&& !this.order.consignor.name
				&& !this.order.consignor.postalCode
				&& !this.order.consignor.postalPlace
			)
				this.$emit('changeConsignor', customer);

			if (
				!this.order.despatchParty.id
				&& !this.order.despatchParty.name
				&& !this.order.despatchParty.postalCode
				&& !this.order.despatchParty.postalPlace
			)
				this.$emit('changeDespatchParty', customer);
		},

		onChangeConsignor(consignor) {
			this.$emit('changeConsignor', consignor);

			if (!this.order.despatchParty.id) {
				this.$emit('changeDespatchParty', consignor);

				// Need this because otherwise the text in despatch party input will not be selected. See updated()
				this.shouldSelectAllDespatchParty = true;
			}

			if (
				this.incoterm
				&& this.incoterm.getCustomerFrom === 'S' // S for avSender
				&& consignor.customerNumber
			)
				this.$emit('updateCustomerByNumber', consignor.customerNumber);

			if (
				this.incoterm
				&& this.incoterm.getAddressFrom === 'S' // S for avSender
				&& consignor.postalPlace
			)
				this.$emit('setIncotermsPlace', consignor.postalPlace);
		},

		onChangeDespatchParty(despatchParty) {
			if (this.ediSent || this.actualDatesSet) {
				this.address = despatchParty;
				this.originalAddress = this.order.despatchParty;
				this.emitCode = 'changeDespatchParty';
				this.confirmEditAddressVisible = true;
			} else {
				this.$emit('changeDespatchParty', despatchParty);
			}
		},

		onChangeConsignee(consignee) {
			this.$emit('changeConsignee', consignee);

			if (!this.order.deliveryParty.id) {
				this.$emit('changeDeliveryParty', consignee);

				// Need this because otherwise the text in delivery party input will not be selected. See updated()
				this.shouldSelectAllDeliveryParty = true;
			}

			if (
				this.incoterm
				&& this.incoterm.getCustomerFrom === 'M' // M for Mottaker
				&& consignee.customerNumber
			)
				this.$emit('updateCustomerByNumber', consignee.customerNumber);

			if (
				this.incoterm
				&& this.incoterm.getAddressFrom === 'M' // M for Mottaker
				&& consignee.postalPlace
			)
				this.$emit('setIncotermsPlace', consignee.postalPlace);
		},

		onChangeDeliveryParty(deliveryParty) {
			if (this.ediSent || this.actualDatesSet) {
				this.address = deliveryParty;
				this.originalAddress = this.order.deliveryParty;
				this.emitCode = 'changeDeliveryParty';
				this.confirmEditAddressVisible = true;
			} else {
				this.$emit('changeDeliveryParty', deliveryParty);
			}
		},
		onConfirmEditAddress() {
			this.$emit(this.emitCode, this.address);
		},
		cancelEditAddress() {
			this.$refs.despatchParty.resetInput();
			this.$refs.deliveryParty.resetInput();
		}
	}
};
</script>

<style lang="scss" scoped>

.container {
	margin-top: 4px;
	margin-bottom: 15px;
	width: 1000px;

	display: grid;
	grid-template-columns: repeat(3, 280px);
	row-gap: 15px;
	column-gap: 10px;
}

.incoterms {
	margin-top: 4px;
}

.incoterms-code {
	margin-bottom: 12px;
}

</style>
